import { useEffect, useMemo } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Loader, QueryBoundary } from '@outdoria/ui';
import Unknown from '@outdoria/ui/assets/icons_new/Unknown.svg';
import { ExistingMarkers } from './ExistingMarkers';
import { Marker } from './Marker';
import { MAP_KEY } from '../../../env';

type Props = {
  coords: [number, number];
  selectedCoords: [number, number];
  selectedMarker: string | null;
  setSelectedCoords: (selectedCoords: [number, number]) => void;
  setSelectedMarker: (selectedMarker: string | null) => void;
  setIsCreateView: (isCreateView: boolean) => void;
  isCreateView: boolean;
  isSatelliteEnabled: boolean;
};
export const InteractiveMap = (props: Props) => {
  return (
    <QueryBoundary fallback={<Loader />}>
      <Load {...props} />
    </QueryBoundary>
  );
};

const Load = ({
  coords,
  selectedCoords,
  selectedMarker,
  setSelectedCoords,
  setSelectedMarker,
  setIsCreateView,
  isCreateView,
  isSatelliteEnabled,
}: Props) => {
  const { attribution, url } = useLeafletMap(
    coords,
    setIsCreateView,
    setSelectedCoords,
    setSelectedMarker,
    isSatelliteEnabled,
  );

  return (
    <>
      <TileLayer attribution={attribution} url={url} />

      <ExistingMarkers
        selectedMarker={selectedMarker}
        setIsCreateView={setIsCreateView}
        setSelectedMarker={setSelectedMarker}
      />

      {isCreateView && (
        <Marker
          iconUrl={Unknown}
          isSelected={false}
          markerId="current"
          position={L.latLng(selectedCoords)}
          onClick={() => {}}
        />
      )}
    </>
  );
};

const useLeafletMap = (
  coords: [number, number],
  setIsCreateView: (isCreateView: boolean) => void,
  setSelectedCoords: (selectedCoords: [number, number]) => void,
  setSelectedMarker: (selectedMarker: string | null) => void,
  isSatelliteEnabled: boolean,
) => {
  const map = useMap();

  const { attribution, url } = useMemo(() => {
    if (isSatelliteEnabled)
      return {
        attribution:
          '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
        url: `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${MAP_KEY}`,
      };
    else
      return {
        attribution:
          '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      };
  }, [isSatelliteEnabled]);

  /**
   * Resize map on any map change (important, otherwise map tiles break)
   */
  useEffect(() => {
    map.invalidateSize();
  }, [map]);

  /**
   * Center map on selected coords once they are selected
   */
  useEffect(() => {
    if (map) {
      map.flyTo(L.latLng(coords[0], coords[1]));
    }
  }, [coords, map]);

  /**
   * Bind map click event.
   * User gets moved to create marker mode no matter if they were on empty map
   * or were editing existing marker.
   */
  useEffect(() => {
    const mapClickEvent = (ev: L.LeafletMouseEvent) => {
      const latlng = map.mouseEventToLatLng(ev.originalEvent);
      setSelectedCoords([latlng.lat, latlng.lng]);
      setIsCreateView(true);
      setSelectedMarker(null);
    };

    map.on('click', mapClickEvent);

    return () => {
      map.removeEventListener('click', mapClickEvent);
    };
  }, []);

  return { attribution, url };
};
